'use client';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import CCPALink from '@/components/CCPALink';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    conservation: [
    { label: 'Impact Dashboard', href: '/impact-dashboard-homepage' },
    { label: 'Conservation Data', href: '/conservation-data-center' },
    { label: 'Podcast', href: '/podcasts' },
    { label: 'News and Updates', href: '/news-updates' }],

    resources: [
    { label: 'News & Updates', href: '/news-updates' },
    { label: 'Research Publications', href: '/conservation-data-center#research-publications' },
    { label: 'Conservation Data Center', href: '/conservation-data-center' },
    { label: 'Podcasts', href: '/podcasts' }],

    support: [
    { label: 'Donate Now', href: '/impact-dashboard-homepage' }],

    legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Cookie Policy', href: '/cookies' },
    { label: 'Financial Transparency', href: '/donations' },
    { label: 'Contact Us', href: '/contact-us' }]

  };

  const socialLinks = [
  { icon: 'ShareIcon', label: 'Facebook', href: '#' },
  { icon: 'ShareIcon', label: 'Twitter', href: '#' },
  { icon: 'ShareIcon', label: 'Instagram', href: '#' },
  { icon: 'ShareIcon', label: 'LinkedIn', href: '#' }];

  const handleCookiePreferences = () => {
    // Trigger cookie preferences modal
    const event = new CustomEvent('openCookiePreferences');
    window.dispatchEvent(event);
  };

  return (
    <footer className="bg-primary text-primary-foreground pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-1">
            <Link href="/impact-dashboard-homepage" className="flex items-center space-x-3 mb-4">
              <div className="relative w-10 h-10">
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <path
                    d="M20 5C11.716 5 5 11.716 5 20C5 28.284 11.716 35 20 35C28.284 35 35 28.284 35 20C35 11.716 28.284 5 20 5Z"
                    fill="var(--color-warning)" />

                  <path
                    d="M15 18C15 16.343 16.343 15 18 15C19.657 15 21 16.343 21 18C21 19.657 19.657 21 18 21C16.343 21 15 19.657 15 18Z"
                    fill="var(--color-primary)" />

                  <path
                    d="M25 15C23.343 15 22 16.343 22 18C22 19.657 23.343 21 25 21C26.657 21 28 19.657 28 18C28 16.343 26.657 15 25 15Z"
                    fill="var(--color-primary)" />

                  <path
                    d="M12 25C12 23.5 14 22 20 22C26 22 28 23.5 28 25V27H12V25Z"
                    fill="var(--color-primary)" />

                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-headline font-bold leading-tight">Rhinos&Rangers</span>
                <span className="text-xs opacity-80 font-body">Conservation Intelligence</span>
              </div>
            </Link>
            <p className="text-sm font-body opacity-90 mb-4">Protecting Africa's rhinos through the latest data, community engagement, and strategic conservation action.

            </p>
            <div className="flex items-center space-x-3">
              {socialLinks?.map((social, index) =>
              <a
                key={index}
                href={social?.href}
                aria-label={social?.label}
                className="p-2 bg-primary-foreground/10 rounded-lg hover:bg-primary-foreground/20 transition-organic duration-300">

                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Conservation</h3>
            <ul className="space-y-2">
              {footerLinks?.conservation?.map((link, index) =>
              <li key={index}>
                  <Link
                  href={link?.href}
                  className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300">

                    {link?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks?.resources?.map((link, index) =>
              <li key={index}>
                  <Link
                  href={link?.href}
                  className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300">

                    {link?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Support Us</h3>
            <ul className="space-y-2">
              {footerLinks?.support?.map((link, index) =>
              <li key={index}>
                  <Link
                  href={link?.href}
                  className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300">

                    {link?.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-base font-headline font-bold mb-4">Legal</h3>
            <ul className="space-y-2">
              {footerLinks?.legal?.map((link, index) =>
              <li key={index}>
                  <Link
                  href={link?.href}
                  className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300">

                    {link?.label}
                  </Link>
                </li>
              )}
              <li>
                <CCPALink />
              </li>
              <li>
                <button
                  onClick={handleCookiePreferences}
                  className="text-sm font-body opacity-80 hover:opacity-100 transition-organic duration-300 text-left"
                >
                  Cookie Preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-primary-foreground/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm font-body opacity-80">
              &copy; {currentYear} Rhinos&Rangers Conservation Initiative. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={16} variant="solid" className="opacity-80" />
                <span className="text-xs font-body opacity-80">Verified Conservation Organization</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="LockClosedIcon" size={16} variant="solid" className="opacity-80" />
                <span className="text-xs font-body opacity-80">Secure Donations</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;